/* eslint-disable */
/**
 * Lightweight native select helper used by legacy select setup modules.
 * It keeps existing Vue pages compiling while using native <select>/<input>
 * behavior instead of the old external Choices bundle.
 */
export class Choices {
  constructor(elementOrSelector, config = {}) {
    this.config = config;
    this.element = typeof elementOrSelector === 'string'
      ? document.querySelector(elementOrSelector)
      : elementOrSelector;
    this.passedElement = { element: this.element };
    this._listeners = [];
    this._destroyed = false;

    if (this.element) {
      this.element.classList.add('native-choices');
      if (config.searchEnabled !== false) this.element.dataset.nativeSearch = 'true';

      const fireShowDropdown = () => {
        if (this._firingShowDropdown) return;
        this._firingShowDropdown = true;
        try { this.element.dispatchEvent(new CustomEvent('showDropdown', { bubbles: false })); }
        finally { this._firingShowDropdown = false; }
      };
      this.element.addEventListener('focus', fireShowDropdown);
      this.element.addEventListener('mousedown', fireShowDropdown);
      this._listeners.push(['focus', fireShowDropdown], ['mousedown', fireShowDropdown]);

      // The real Choices.js constructor accepts an initial `choices` array.
      // Our lightweight wrapper must honour that too; otherwise empty native
      // selects render as blank/hidden-looking controls inside modals.
      if (Array.isArray(config.choices) && config.choices.length) {
        this.setChoices(config.choices, 'value', 'label', true);
      }
    }
  }

  async setChoices(choices, valueKey = 'value', labelKey = 'label', replaceChoices = false) {
    if (!this.element) return this;
    const list = typeof choices === 'function' ? await choices() : choices;
    const items = Array.isArray(list) ? list : [];

    if (replaceChoices && 'options' in this.element) {
      this.element.innerHTML = '';
    }

    if ('options' in this.element) {
      const frag = document.createDocumentFragment();
      items.forEach(item => {
        if (item == null) return;
        const opt = document.createElement('option');
        const value = typeof item === 'object' ? item[valueKey] : item;
        const label = typeof item === 'object' ? item[labelKey] : item;
        opt.value = value == null ? '' : String(value);
        opt.textContent = label == null ? '' : String(label);
        if (item?.selected) opt.selected = true;
        if (item?.disabled) opt.disabled = true;
        frag.appendChild(opt);
      });
      this.element.appendChild(frag);
      this.element.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (items.length && this.element.tagName === 'INPUT') {
      // For legacy text inputs, keep the first label/value as the input value.
      const first = items[0];
      this.element.value = typeof first === 'object'
        ? String(first[labelKey] ?? first[valueKey] ?? '')
        : String(first ?? '');
    }

    return this;
  }

  clearStore() { return this.clearChoices(); }

  clearChoices() {
    if (this.element && 'options' in this.element) this.element.innerHTML = '';
    return this;
  }

  removeActiveItems() {
    if (!this.element) return this;
    if ('selectedIndex' in this.element) this.element.selectedIndex = -1;
    else this.element.value = '';
    this.element.dispatchEvent(new Event('change', { bubbles: true }));
    return this;
  }

  setChoiceByValue(value) {
    if (!this.element) return this;
    this.element.value = value;
    this.element.dispatchEvent(new Event('change', { bubbles: true }));
    return this;
  }

  getValue(valueOnly = false) {
    if (!this.element) return valueOnly ? '' : null;

    const toItem = (optionOrValue) => {
      if (optionOrValue && typeof optionOrValue === 'object' && 'value' in optionOrValue) {
        return {
          value: optionOrValue.value,
          label: optionOrValue.textContent || optionOrValue.label || optionOrValue.value,
          selected: !!optionOrValue.selected,
          disabled: !!optionOrValue.disabled
        };
      }
      return {
        value: this.element.value || '',
        label: this.element.value || '',
        selected: true,
        disabled: !!this.element.disabled
      };
    };

    if ('selectedOptions' in this.element && this.element.multiple) {
      const selected = Array.from(this.element.selectedOptions || []);
      return valueOnly ? selected.map(o => o.value) : selected.map(toItem);
    }

    if ('selectedOptions' in this.element) {
      const selected = this.element.selectedOptions && this.element.selectedOptions[0];
      if (valueOnly) return selected ? selected.value : (this.element.value || '');
      return selected ? toItem(selected) : null;
    }

    return valueOnly ? (this.element.value || '') : toItem(this.element.value || '');
  }

  disable() { if (this.element) this.element.disabled = true; return this; }
  enable() { if (this.element) this.element.disabled = false; return this; }

  destroy() {
    if (this.element) this.element.classList.remove('native-choices');
    this._listeners.forEach(([event, fn]) => this.element?.removeEventListener(event, fn));
    this._listeners = [];
    this._destroyed = true;
  }
}

export default Choices;
